import React, { useState } from 'react';
import { 
  Activity, Clock, User, CheckCircle2, Search, Filter, Plus, 
  FileText, ShieldCheck, Stethoscope, Sparkles, AlertCircle, Printer
} from 'lucide-react';
import { INITIAL_TREATMENT_AUDIT_LOGS, TreatmentSessionLog } from '../data/treatmentAuditStore';
import { DEMO_STUDENTS } from '../data/demoStudents';

export const TreatmentAuditTracker: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<TreatmentSessionLog[]>(INITIAL_TREATMENT_AUDIT_LOGS);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // New Log Form State
  const [studentId, setStudentId] = useState(DEMO_STUDENTS[0].id);
  const [moduleKey, setModuleKey] = useState('aac');
  const [moduleName, setModuleName] = useState('01. AAC Speech Synthesizer');
  const [therapistName, setTherapistName] = useState('Sarah Jenkins (SLP)');
  const [durationMins, setDurationMins] = useState(45);
  const [scoreAchieved, setScoreAchieved] = useState('88% Goal Mastery');
  const [observation, setObservation] = useState('');
  const [cptCode, setCptCode] = useState('CPT 92507 (Speech Therapy)');
  const [status, setStatus] = useState<'ACTIVE_SESSION' | 'COMPLETED'>('COMPLETED');

  const handleAddSession = (e: React.FormEvent) => {
    e.preventDefault();
    const st = DEMO_STUDENTS.find(s => s.id === studentId) || DEMO_STUDENTS[0];

    const newLog: TreatmentSessionLog = {
      id: `LOG-2026-00${auditLogs.length + 1}`,
      studentId: st.id,
      studentName: st.name,
      rollId: st.rollId,
      moduleKey: moduleKey,
      moduleName: moduleName,
      therapistName: therapistName,
      sessionDate: new Date().toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }),
      durationMins: durationMins,
      status: status,
      scoreAchieved: scoreAchieved,
      treatmentObservation: observation || 'Clinical treatment session completed cleanly.',
      cptCode: cptCode
    };

    setAuditLogs([newLog, ...auditLogs]);
    setIsAddModalOpen(false);
    setObservation('');
  };

  const filteredLogs = auditLogs.filter(log => {
    const matchesSearch = log.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.rollId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.moduleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.therapistName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || log.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const activeSessionsCount = auditLogs.filter(l => l.status === 'ACTIVE_SESSION').length;
  const completedSessionsCount = auditLogs.filter(l => l.status === 'COMPLETED').length;

  return (
    <div className="space-y-6">
      
      {/* Audit Header Summary Bar */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white p-6 rounded-3xl shadow-xl border border-blue-500/30 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="space-y-2 text-center md:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-extrabold rounded-full border border-emerald-500/30 uppercase tracking-wider">
            <Activity className="w-4 h-4 text-emerald-400" />
            <span>Clinical Treatment Audit & Session Tracker</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black">
            Active Therapy Session Audit Log
          </h2>
          <p className="text-xs text-blue-200">
            Real-time tracking of which students are receiving clinical treatment across all 30 AbilityOS modules.
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white/10 p-3 rounded-2xl border border-white/10 backdrop-blur-md shrink-0">
          <div className="text-center px-3 border-r border-white/10">
            <div className="text-2xl font-black text-amber-400 font-mono animate-pulse">{activeSessionsCount}</div>
            <div className="text-[10px] text-blue-200">Active In-Session</div>
          </div>
          <div className="text-center px-3">
            <div className="text-2xl font-black text-emerald-400 font-mono">{completedSessionsCount}</div>
            <div className="text-[10px] text-blue-200">Completed Sessions</div>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs rounded-xl shadow transition flex items-center gap-1.5 ml-2"
          >
            <Plus className="w-4 h-4" />
            <span>Record Session</span>
          </button>
        </div>
      </div>

      {/* Filter & Search Controls */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search student, module, therapist..."
            className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs font-bold text-slate-500">Filter Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-800"
          >
            <option value="ALL">All Session Statuses</option>
            <option value="ACTIVE_SESSION">Active In-Session</option>
            <option value="COMPLETED">Completed Sessions</option>
          </select>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-extrabold uppercase text-slate-600">
                <th className="p-3.5">Session ID / Date</th>
                <th className="p-3.5">Student Name</th>
                <th className="p-3.5">Module Used</th>
                <th className="p-3.5">Therapist</th>
                <th className="p-3.5">Duration</th>
                <th className="p-3.5">Status</th>
                <th className="p-3.5">Score / Observation</th>
                <th className="p-3.5">CPT Code</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50 transition">
                  <td className="p-3.5">
                    <div className="font-mono font-bold text-slate-800">{log.id}</div>
                    <div className="text-[10px] text-slate-500">{log.sessionDate}</div>
                  </td>

                  <td className="p-3.5">
                    <div className="font-bold text-slate-900">{log.studentName}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{log.rollId}</div>
                  </td>

                  <td className="p-3.5 font-semibold text-blue-700">
                    {log.moduleName}
                  </td>

                  <td className="p-3.5 font-medium text-slate-700">
                    {log.therapistName}
                  </td>

                  <td className="p-3.5 font-mono font-bold text-slate-800">
                    {log.durationMins} Min
                  </td>

                  <td className="p-3.5">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold inline-flex items-center gap-1.5 ${
                      log.status === 'ACTIVE_SESSION' 
                        ? 'bg-amber-100 text-amber-900 border border-amber-300 animate-pulse' 
                        : 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${log.status === 'ACTIVE_SESSION' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                      {log.status.replace('_', ' ')}
                    </span>
                  </td>

                  <td className="p-3.5 space-y-0.5">
                    <div className="font-bold text-emerald-700">{log.scoreAchieved}</div>
                    <div className="text-[11px] text-slate-600 line-clamp-1">{log.treatmentObservation}</div>
                  </td>

                  <td className="p-3.5 font-mono text-[10px] font-bold text-slate-600">
                    {log.cptCode || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add New Session Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-900 to-indigo-900 p-5 text-white flex justify-between items-center">
              <h3 className="font-extrabold text-base flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-400" />
                Record Live Clinical Treatment Session
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-blue-200 hover:text-white font-bold">✕</button>
            </div>

            <form onSubmit={handleAddSession} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Select Patient / Student *</label>
                <select
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-xl font-bold text-slate-800 focus:ring-2 focus:ring-blue-500"
                >
                  {DEMO_STUDENTS.map(st => (
                    <option key={st.id} value={st.id}>{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Module Name</label>
                  <input
                    type="text"
                    value={moduleName}
                    onChange={(e) => setModuleName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-xl font-bold"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Therapist Name</label>
                  <input
                    type="text"
                    value={therapistName}
                    onChange={(e) => setTherapistName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-xl font-bold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Duration (Minutes)</label>
                  <input
                    type="number"
                    value={durationMins}
                    onChange={(e) => setDurationMins(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-xl font-bold"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Score / Progress</label>
                  <input
                    type="text"
                    value={scoreAchieved}
                    onChange={(e) => setScoreAchieved(e.target.value)}
                    className="w-full px-3 py-2 border rounded-xl font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Treatment Observation Notes *</label>
                <textarea
                  required
                  rows={3}
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  placeholder="Record patient progress, AAC selections, motor gait notes..."
                  className="w-full px-3 py-2 border rounded-xl font-medium focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl">Cancel</button>
                <button type="submit" className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow">Log Session</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
